import { AuthRestfulService } from "./auth-restful.service";

export type RabbitMqSessionInfo = {
  authenticated: boolean;
  auth_token?: string;
  rabbitmq_username?: string;
  can_view_system?: boolean;
  can_view_other_users?: boolean;
  available_main_topics?: string[];
};

const MQTTLOG_AUTH_TOKEN_KEY = "mqttlog.authToken";

export type MqttLogPreviewItem = {
  timestamp: string;
  level: string;
  event: "publish" | "deliver";
  client_addr: string;
  trace_route: string;
  routed_queues: string[];
  routing_key: string;
  main_topic: string | null;
  sub_topic: string | null;
  rabbitmq_username: string;
  payload: string;
  payload_json: Record<string, unknown> | null;
  raw_line: string;
};

export type MqttLogPreviewResponse = {
  items: MqttLogPreviewItem[];
  count: number;
  limit: number;
  has_more: boolean;
  scan_summary: MqttLogScanSummary;
  effective_filters: {
    start: string | null;
    end: string | null;
    event: string | null;
    main_topic: string | null;
    sub_topic: string | null;
    client_id: string | null;
    publisher_username: string | null;
    limit: number;
  };
  access_scope: {
    rabbitmq_username: string;
    can_view_system: boolean;
    can_view_other_users: boolean;
    available_main_topics?: string[];
  };
};

export type MqttLogScanSummary = {
  estimated_seconds: number;
  file_count: number;
};

export type MqttLogEstimateResponse = {
  preview_scan_summary: MqttLogScanSummary;
  download_scan_summary: MqttLogScanSummary;
  effective_filters: MqttLogPreviewResponse["effective_filters"];
  access_scope: MqttLogPreviewResponse["access_scope"];
};

export type MqttLogJobStatus =
  | "queued"
  | "running"
  | "done"
  | "failed"
  | "canceled";

export type MqttLogJobStartResponse = {
  job_id: string;
  status: MqttLogJobStatus;
  expires_at: number;
};

export type MqttLogJobStatusResponse = {
  job_id: string;
  type: "preview" | "download";
  status: MqttLogJobStatus;
  message: string;
  expires_at: number;
};

export type MqttLogQuery = {
  start?: string;
  end?: string;
  event?: "publish" | "deliver";
  mainTopic?: string;
  subTopic?: string;
  clientId?: string;
  publisherUsername?: string;
  limit?: number;
};

class MqttLogApiService extends AuthRestfulService {
  private getAuthToken(): string {
    try {
      return window.localStorage.getItem(MQTTLOG_AUTH_TOKEN_KEY) || "";
    } catch {
      return "";
    }
  }

  private storeAuthToken(token?: string): void {
    try {
      if (!token) {
        window.localStorage.removeItem(MQTTLOG_AUTH_TOKEN_KEY);
        return;
      }
      window.localStorage.setItem(MQTTLOG_AUTH_TOKEN_KEY, token);
    } catch {
      // Ignore storage errors and fall back to in-memory API usage.
    }
  }

  private buildAuthConfig() {
    const token = this.getAuthToken();
    if (!token) {
      return undefined;
    }

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  }

  public getAuthorizationHeaderValue(): string | null {
    const token = this.getAuthToken();
    return token ? `Bearer ${token}` : null;
  }

  public getAuthHeaders(): Record<string, string> | undefined {
    const authorization = this.getAuthorizationHeaderValue();
    if (!authorization) {
      return undefined;
    }

    return {
      Authorization: authorization,
    };
  }

  public async getSession(): Promise<RabbitMqSessionInfo> {
    if (!this.getAuthToken()) {
      return { authenticated: false };
    }

    const response = await this.doGet(
      "/mqttlog/session",
      undefined,
      this.buildAuthConfig(),
    );

    if (response?.authenticated !== true) {
      this.storeAuthToken();
    }

    return response;
  }

  public async authenticateRabbit(request: {
    rabbitmqUsername: string;
    rabbitmqPassword: string;
  }): Promise<RabbitMqSessionInfo> {
    const response = await this.doPost("/mqttlog/session", {
      rabbitmq_username: request.rabbitmqUsername,
      rabbitmq_password: request.rabbitmqPassword,
    });

    this.storeAuthToken(response?.auth_token);
    return response;
  }

  public async clearRabbitSession(): Promise<RabbitMqSessionInfo> {
    const response = await this.requestDelete(
      "/mqttlog/session",
      this.buildAuthConfig(),
    );
    this.storeAuthToken();
    return response;
  }

  public async preview(
    request: MqttLogQuery,
    options?: { signal?: AbortSignal },
  ): Promise<MqttLogPreviewResponse> {
    const response = await this.doGet(
      "/mqttlog/preview",
      {
        start: request.start,
        end: request.end,
        event: request.event,
        main_topic: request.mainTopic,
        sub_topic: request.subTopic,
        client_id: request.clientId,
        publisher_username: request.publisherUsername,
        limit: request.limit,
      },
      {
        ...this.buildAuthConfig(),
        signal: options?.signal,
      },
    );
    return response;
  }

  public async startPreviewJob(
    request: MqttLogQuery,
    options?: { signal?: AbortSignal },
  ): Promise<MqttLogJobStartResponse> {
    const response = await this.doPost(
      "/mqttlog/preview-job",
      {
        start: request.start,
        end: request.end,
        event: request.event,
        main_topic: request.mainTopic,
        sub_topic: request.subTopic,
        client_id: request.clientId,
        publisher_username: request.publisherUsername,
        limit: request.limit,
      },
      {
        ...this.buildAuthConfig(),
        signal: options?.signal,
      },
    );
    return response;
  }

  public async getPreviewJob(
    jobId: string,
    options?: { signal?: AbortSignal },
  ): Promise<MqttLogJobStatusResponse> {
    const response = await this.doGet(
      `/mqttlog/preview-job/${encodeURIComponent(jobId)}`,
      undefined,
      {
        ...this.buildAuthConfig(),
        signal: options?.signal,
      },
    );
    return response;
  }

  public async getPreviewJobResult(
    jobId: string,
    options?: { signal?: AbortSignal },
  ): Promise<MqttLogPreviewResponse> {
    const response = await this.doGet(
      `/mqttlog/preview-job/${encodeURIComponent(jobId)}/result`,
      undefined,
      {
        ...this.buildAuthConfig(),
        signal: options?.signal,
      },
    );
    return response;
  }

  public async cancelPreviewJob(
    jobId: string,
  ): Promise<MqttLogJobStatusResponse> {
    const response = await this.doPost(
      `/mqttlog/preview-job/${encodeURIComponent(jobId)}/cancel`,
      undefined,
      this.buildAuthConfig(),
    );
    return response;
  }

  public async estimate(
    request: MqttLogQuery,
    options?: { signal?: AbortSignal },
  ): Promise<MqttLogEstimateResponse> {
    const response = await this.doGet(
      "/mqttlog/estimate",
      {
        start: request.start,
        end: request.end,
        event: request.event,
        main_topic: request.mainTopic,
        sub_topic: request.subTopic,
        client_id: request.clientId,
        publisher_username: request.publisherUsername,
      },
      {
        ...this.buildAuthConfig(),
        signal: options?.signal,
      },
    );
    return response;
  }

  public async startDownloadJob(
    request: MqttLogQuery,
    options?: { signal?: AbortSignal },
  ): Promise<MqttLogJobStartResponse> {
    const response = await this.doPost(
      "/mqttlog/download-job",
      {
        start: request.start,
        end: request.end,
        event: request.event,
        main_topic: request.mainTopic,
        sub_topic: request.subTopic,
        client_id: request.clientId,
        publisher_username: request.publisherUsername,
      },
      {
        ...this.buildAuthConfig(),
        signal: options?.signal,
      },
    );
    return response;
  }

  public async getDownloadJob(
    jobId: string,
    options?: { signal?: AbortSignal },
  ): Promise<MqttLogJobStatusResponse> {
    const response = await this.doGet(
      `/mqttlog/download-job/${encodeURIComponent(jobId)}`,
      undefined,
      {
        ...this.buildAuthConfig(),
        signal: options?.signal,
      },
    );
    return response;
  }

  public async cancelDownloadJob(
    jobId: string,
  ): Promise<MqttLogJobStatusResponse> {
    const response = await this.doPost(
      `/mqttlog/download-job/${encodeURIComponent(jobId)}/cancel`,
      undefined,
      this.buildAuthConfig(),
    );
    return response;
  }

  public getDownloadJobFileUrl(jobId: string): string {
    const base = String(import.meta.env.VITE_API_URL || "").replace(/\/$/, "");
    return `${base}/mqttlog/download-job/${encodeURIComponent(jobId)}/file`;
  }

  public getDownloadJobFileRequestInit(): RequestInit {
    return {
      headers: this.getAuthHeaders(),
    };
  }

  public getDownloadRequestInit(): RequestInit {
    const config = this.buildAuthConfig();

    return {
      headers: config?.headers,
    };
  }

  public getDownloadUrl(request: MqttLogQuery): string {
    const base = String(import.meta.env.VITE_API_URL || "").replace(/\/$/, "");
    const params = new URLSearchParams();

    if (request.start) {
      params.set("start", request.start);
    }
    if (request.end) {
      params.set("end", request.end);
    }
    if (request.event) {
      params.set("event", request.event);
    }
    if (request.mainTopic) {
      params.set("main_topic", request.mainTopic);
    }
    if (request.subTopic) {
      params.set("sub_topic", request.subTopic);
    }
    if (request.clientId) {
      params.set("client_id", request.clientId);
    }
    if (request.publisherUsername) {
      params.set("publisher_username", request.publisherUsername);
    }

    return `${base}/mqttlog/download?${params.toString()}`;
  }
}

export const mqttLogApiService = new MqttLogApiService();
export default mqttLogApiService;
